import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { OnboardingState, OnboardingStepProps } from './types';
import { StepPain } from './StepPain';
import { StepRole } from './StepRole';
import { StepBar } from './StepBar';
import { StepCandidate } from './StepCandidate';
import { StepAISignal } from './StepAISignal';
import { StepDecision } from './StepDecision';
import { StepSuccess } from './StepSuccess';

const STEPS = [
  { id: 'pain', label: 'Challenge', component: StepPain },
  { id: 'role', label: 'Role', component: StepRole },
  { id: 'bar', label: 'Bar', component: StepBar },
  { id: 'candidate', label: 'Candidate', component: StepCandidate },
  { id: 'signal', label: 'Signal', component: StepAISignal },
  { id: 'decision', label: 'Decision', component: StepDecision },
] as const;

const initialState: OnboardingState = {
  painPoint: null,
  role: null,
  bar: null,
  candidate: null,
  signal: null,
  decision: null,
};

export default function GetStartedPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [state, setState] = useState<OnboardingState>(initialState);
  const [isComplete, setIsComplete] = useState(false);

  const handleUpdate = useCallback((updates: Partial<OnboardingState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep === STEPS.length - 1) {
      setIsComplete(true);
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep]);

  const handleBack = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, []);

  if (isComplete) {
    return <StepSuccess state={state} />;
  }

  const CurrentStepComponent = STEPS[currentStep].component;
  const stepProps: OnboardingStepProps = {
    state,
    onUpdate: handleUpdate,
    onNext: handleNext,
    onBack: handleBack,
  };

  // Calculate progress percentage for the animated bar
  const progressPercent = ((currentStep) / (STEPS.length - 1)) * 100;

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden">
      {/* Ambient background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-brand-50/30" />
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-brand-100/40 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-cyan-50/50 via-transparent to-transparent rounded-full blur-3xl" />
        {/* Subtle grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Header */}
      <header className="border-b border-surface-200/60 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img src="/images/logo.png" alt="JumpstoneHR" className="w-8 h-8 transition-transform group-hover:scale-105" />
            <span className="font-display font-semibold text-lg text-surface-900">Jumpstone</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-surface-500 hover:text-surface-700 transition-colors group"
          >
            <span>Exit</span>
            <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
            </svg>
          </Link>
        </div>
      </header>

      {/* Progress section */}
      <div className="bg-white/50 backdrop-blur-sm border-b border-surface-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          {/* Progress bar - slim and elegant */}
          <div className="mb-4">
            <div className="h-1 bg-surface-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-brand-500 to-brand-600 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
          
          {/* Step indicators */}
          <div className="flex items-center justify-between gap-1">
            {STEPS.map((step, index) => {
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;

              return (
                <div key={step.id} className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={`
                        w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300
                        ${isActive
                          ? 'bg-brand-600 text-white ring-4 ring-brand-100'
                          : isCompleted
                            ? 'bg-emerald-500 text-white'
                            : 'bg-surface-100 text-surface-400 border border-surface-200'
                        }
                      `}
                    >
                      {isCompleted ? (
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>
                    <span
                      className={`
                        hidden md:block text-sm font-medium transition-colors
                        ${isActive ? 'text-surface-900' : isCompleted ? 'text-surface-600' : 'text-surface-400'}
                      `}
                    >
                      {step.label}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className="hidden sm:block flex-1 mx-2">
                      <div className="w-full h-px bg-surface-200" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 py-10 sm:py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <CurrentStepComponent {...stepProps} />
        </div>
      </main>

      {/* Footer hint */}
      <footer className="border-t border-surface-100 bg-white/60 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center gap-2 text-sm text-surface-400">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
            <span>Step {currentStep + 1} of {STEPS.length}</span>
            <span className="text-surface-300">·</span>
            <span>Progress saved</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
